import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { LogoWordmark } from '@/components/brand/LogoWordmark'
import { halftone } from '@/components/landing/service/halftone'
import {
  ArrowRight,
  ChevronDown,
  Menu,
  X,
  Briefcase,
  Megaphone,
  Building2,
  Calculator,
  Sparkles,
  FileText,
  FolderOpen,
  BarChart3,
  Zap,
  Shield,
  Plug,
  Building,
  Heart,
  Mail,
  Scale,
  TrendingUp,
  Stethoscope,
  HardHat,
} from 'lucide-react'

interface NavChild {
  label: string
  href: string
  description?: string
  icon?: React.ElementType
}

interface NavSection {
  title?: string
  items: NavChild[]
}

interface NavTile {
  title: string
  href?: string
  links: NavChild[]
}

interface NavItem {
  label: string
  href?: string
  megaMenu?: boolean
  children?: NavChild[]
  columns?: NavChild[][]
  sections?: NavSection[]
  /** blue2purple-style mega menu: four flat rectangle tiles. */
  tiles?: NavTile[]
  featured?: {
    title: string
    description: string
    href: string
    cta: string
  }
}

const navigation: NavItem[] = [
  {
    label: 'Solutions',
    megaMenu: true,
    sections: [
      {
        title: 'By Team',
        items: [
          {
            label: 'Sales Teams',
            href: '/solutions/sales',
            description: 'Win more deals, faster',
            icon: Briefcase,
          },
          {
            label: 'Marketing & Brand',
            href: '/solutions/marketing',
            description: 'Protect your brand without bottlenecks',
            icon: Megaphone,
          },
          {
            label: 'Operations & Admin',
            href: '/solutions/operations',
            description: 'Eliminate the paperwork',
            icon: Building2,
          },
          {
            label: 'Finance & Accounting',
            href: '/solutions/finance',
            description: 'Models you can trust',
            icon: Calculator,
          },
        ],
      },
      {
        title: 'By Industry',
        items: [
          {
            label: 'Legal',
            href: '/industries/legal',
            description: 'Contract review, briefs, compliance',
            icon: Scale,
          },
          {
            label: 'Finance & Banking',
            href: '/industries/finance',
            description: 'Reports, filings, analysis',
            icon: TrendingUp,
          },
          {
            label: 'Healthcare',
            href: '/industries/healthcare',
            description: 'Clinical docs, HIPAA compliance',
            icon: Stethoscope,
          },
          {
            label: 'Insurance',
            href: '/industries/insurance',
            description: 'Policies, claims, underwriting',
            icon: Shield,
          },
          {
            label: 'Engineering',
            href: '/industries/engineering',
            description: 'Specs, proposals, project docs',
            icon: HardHat,
          },
          {
            label: 'Consulting',
            href: '/industries/consulting',
            description: 'Proposals, deliverables, reports',
            icon: Briefcase,
          },
        ],
      },
    ],
    tiles: [
      {
        title: 'For teams',
        links: [
          { label: 'Sales Teams', href: '/solutions/sales' },
          { label: 'Marketing & Brand', href: '/solutions/marketing' },
          { label: 'Operations & Admin', href: '/solutions/operations' },
          { label: 'Finance & Accounting', href: '/solutions/finance' },
        ],
      },
      {
        title: 'Legal & compliance',
        links: [
          { label: 'Legal', href: '/industries/legal' },
          { label: 'Insurance', href: '/industries/insurance' },
        ],
      },
      {
        title: 'Finance & health',
        links: [
          { label: 'Finance & Banking', href: '/industries/finance' },
          { label: 'Healthcare', href: '/industries/healthcare' },
        ],
      },
      {
        title: 'Projects & services',
        links: [
          { label: 'Engineering', href: '/industries/engineering' },
          { label: 'Consulting', href: '/industries/consulting' },
        ],
      },
    ],
    featured: {
      title: 'See Templatean in action',
      description: 'Watch how teams save 10+ hours per week on document creation.',
      href: '/register',
      cta: 'Start free trial',
    },
  },
  {
    label: 'Platform',
    megaMenu: true,
    columns: [
      [
        {
          label: 'AI Document Generation',
          href: '/platform/ai-generation',
          description: 'Create complete documents in minutes',
          icon: Sparkles,
        },
        {
          label: 'Template Management',
          href: '/platform/templates',
          description: 'Ensure consistency without bottlenecks',
          icon: FileText,
        },
        {
          label: 'Brand Asset Management',
          href: '/platform/dam',
          description: 'Protect brand integrity automatically',
          icon: FolderOpen,
        },
      ],
      [
        {
          label: 'Excel Intelligence',
          href: '/platform/excel',
          description: 'Build accurate financial models faster',
          icon: BarChart3,
        },
        {
          label: 'Productivity Tools',
          href: '/platform/productivity',
          description: 'Polish documents without manual effort',
          icon: Zap,
        },
        {
          label: 'Security & Privacy',
          href: '/platform/security',
          description: 'Protect sensitive information completely',
          icon: Shield,
        },
      ],
      [
        {
          label: 'Integrations',
          href: '/platform/integrations',
          description: 'Connect with existing business systems',
          icon: Plug,
        },
        {
          label: 'Usage Analytics',
          href: '/platform/analytics',
          description: 'See which templates work and where documents stall',
          icon: BarChart3,
        },
      ],
    ],
    tiles: [
      {
        title: 'Generate',
        links: [
          { label: 'AI Document Generation', href: '/platform/ai-generation' },
          { label: 'Productivity Tools', href: '/platform/productivity' },
        ],
      },
      {
        title: 'Govern',
        links: [
          { label: 'Template Management', href: '/platform/templates' },
          { label: 'Brand Asset Management', href: '/platform/dam' },
        ],
      },
      {
        title: 'Analyze',
        links: [
          { label: 'Excel Intelligence', href: '/platform/excel' },
          { label: 'Usage Analytics', href: '/platform/analytics' },
        ],
      },
      {
        title: 'Trust',
        links: [
          { label: 'Security & Privacy', href: '/platform/security' },
          { label: 'Integrations', href: '/platform/integrations' },
        ],
      },
    ],
    featured: {
      title: 'New: Excel Intelligence',
      description: 'Build accurate financial models 5x faster with AI-powered formula assistance.',
      href: '/platform/excel',
      cta: 'Learn more',
    },
  },
  {
    label: 'Pricing',
    href: '/pricing',
  },
  {
    label: 'About',
    megaMenu: true,
    tiles: [
      {
        title: 'Company',
        href: '/about',
        links: [{ label: 'Our purpose and story', href: '/about' }],
      },
      {
        title: 'Careers',
        href: '/about#careers',
        links: [{ label: 'Join the team', href: '/about#careers' }],
      },
      {
        title: 'Contact',
        href: '/contact',
        links: [{ label: 'Get in touch', href: '/contact' }],
      },
    ],
    children: [
      {
        label: 'About Us',
        href: '/about',
        description: 'Our purpose and story',
        icon: Building,
      },
      {
        label: 'Careers',
        href: '/about#careers',
        description: 'Join our team',
        icon: Heart,
      },
      {
        label: 'Contact',
        href: '/contact',
        description: 'Get in touch',
        icon: Mail,
      },
    ],
  },
]

/** Halftone-dot arrow: marks a menu link as a destination. */
function DottedArrow({ className }: { className?: string }) {
  // 7x7 grid: shaft along the middle row, chevron head on the diagonals.
  const dots: Array<[number, number]> = [
    [0, 3], [1, 3], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3],
    [3, 0], [4, 1], [5, 2], [5, 4], [4, 5], [3, 6],
  ]
  return (
    <svg viewBox="0 0 16 16" aria-hidden className={cn('h-3 w-3 shrink-0', className)}>
      {dots.map(([c, r]) => (
        <circle key={`${c}-${r}`} cx={2 + c * 2} cy={2 + r * 2} r={0.8} fill="currentColor" />
      ))}
    </svg>
  )
}

/** Solid double chevron (two triangles) shown on a hovered mega-menu tile. */
function DoubleChevron({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 20" aria-hidden className={className} fill="currentColor">
      <path d="M0 0 L11 10 L0 20 Z M12 0 L23 10 L12 20 Z" />
    </svg>
  )
}

interface MegaMenuProps {
  item: NavItem
  isOpen: boolean
  onClose: () => void
}

function MegaMenu({ item, isOpen, onClose }: MegaMenuProps) {
  if (!item.tiles && !item.columns && !item.sections) return null

  // blue2purple layout: the panel sits flush under the nav, spans the full
  // width, and holds four flat 90-degree rectangle tiles — sub-links on top,
  // a big uppercase display title at the bottom of each tile.
  const tiles: NavTile[] =
    item.tiles ??
    (item.sections
      ? item.sections.map((s) => ({ title: s.title ?? '', links: s.items }))
      : (item.columns ?? []).map((col, i) => ({ title: `Column ${i + 1}`, links: col })))

  return (
    // Content layer only: the white surface is the nav's shared sheet, which
    // grows over bar and panel in one motion. Content fades in once the
    // sheet is mostly down and leaves fast, so switching menus crossfades.
    <div
      data-mega-open={isOpen}
      className={cn(
        'absolute left-0 right-0 top-full z-50 transition-[opacity,translate,visibility] ease-[cubic-bezier(0.22,1,0.36,1)]',
        isOpen
          ? 'opacity-100 visible translate-y-0 duration-300 delay-150'
          : 'opacity-0 invisible -translate-y-2 pointer-events-none duration-150 delay-0'
      )}
    >
      <div>
        <div className="landing-container pt-5 pb-8">
          <div className={cn('grid grid-cols-2 gap-4', tiles.length >= 4 ? 'lg:grid-cols-4' : tiles.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2')}>
            {tiles.map((tile, index) => (
              // On hover or keyboard focus the tile turns brand blue with the
              // halftone, its text turns white and a double chevron appears
              // (blue2purple mega menu).
              <div
                key={tile.title}
                className="group/tile relative flex min-h-[260px] flex-col overflow-hidden bg-[#f0f0f0] p-6 transition-colors duration-200 ease-out hover:bg-[var(--color-landing-accent)] focus-within:bg-[var(--color-landing-accent)] dark:bg-[#1d1d26]"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-200 group-hover/tile:opacity-100 group-focus-within/tile:opacity-100"
                  style={halftone(0.28, 6)}
                />
                <ul className="relative space-y-2.5">
                  {tile.links.map((link) => (
                    <li key={link.href}>
                      <Link
                        to={link.href}
                        onClick={onClose}
                        className="group/link inline-flex items-center gap-2 text-sm text-[#5a5a5a] decoration-white/60 underline-offset-4 transition-colors duration-150 hover:underline group-hover/tile:text-white group-focus-within/tile:text-white dark:text-[#adadad]"
                      >
                        {link.label}
                        <DottedArrow />
                      </Link>
                    </li>
                  ))}
                </ul>
                <span className="flex-1" />
                {(() => {
                  const title = (
                    <>
                      <span className="shrink-0 text-[0.6em] text-[#ababab] transition-colors duration-200 group-hover/tile:text-white/60 group-focus-within/tile:text-white/60 dark:text-[#5a5a5a]">
                        {index + 1}.
                      </span>
                      <span className="min-w-0 flex-1">{tile.title}</span>
                      <DoubleChevron className="h-[0.9em] w-[0.9em] shrink-0 self-center -translate-x-1 opacity-0 transition-[opacity,transform] duration-200 ease-out group-hover/tile:translate-x-0 group-hover/tile:opacity-100 group-focus-within/tile:translate-x-0 group-focus-within/tile:opacity-100" />
                    </>
                  )
                  const titleClass =
                    'relative flex items-baseline gap-[0.4em] font-[family-name:var(--font-landing-display)] uppercase text-xl xl:text-2xl leading-[1.25] tracking-[-0.01em] text-[#0a0a0a] transition-colors duration-200 group-hover/tile:text-white group-focus-within/tile:text-white dark:text-white'
                  return tile.href ? (
                    <Link to={tile.href} onClick={onClose} className={titleClass}>
                      {title}
                    </Link>
                  ) : (
                    <span className={titleClass}>{title}</span>
                  )
                })()}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

interface DropdownProps {
  item: NavItem
  isOpen: boolean
  onClose: () => void
}

function NavDropdown({ item, isOpen, onClose }: DropdownProps) {
  if (!item.children) return null

  return (
    <div
      className={cn(
        'absolute top-full left-0 z-50 transition-all duration-200 ease-out',
        isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1 pointer-events-none'
      )}
    >
      <div className="min-w-[240px] bg-white dark:bg-[#141414] border border-t-0 border-[#e5e5e5] dark:border-[#292929]">
        <div className="p-2">
          {item.children.map((child) => (
            <Link
              key={child.href}
              to={child.href}
              onClick={onClose}
              className="block px-4 py-2.5 text-sm font-medium text-[#242424] dark:text-white hover:bg-[#f0f0f0] dark:hover:bg-[#1d1d26] hover:text-[var(--color-landing-accent)] transition-colors duration-150"
            >
              {child.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

interface NavItemComponentProps {
  item: NavItem
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

function NavItemComponent({ item, isOpen, onOpen, onClose }: NavItemComponentProps) {
  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (itemRef.current && !itemRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Simple link without dropdown
  if (!item.children && !item.megaMenu) {
    return (
      <Link
        to={item.href || '/'}
        className="text-[15px] font-medium text-[#616161] hover:text-[#242424] dark:text-[#adadad] dark:hover:text-white transition-colors duration-200 px-4 py-2"
      >
        {item.label}
      </Link>
    )
  }

  return (
    <div ref={itemRef} className={item.megaMenu ? 'static' : 'relative'}>
      <button
        onClick={() => (isOpen ? onClose() : onOpen())}
        onMouseEnter={onOpen}
        className={cn(
          'flex items-center gap-1 text-[15px] font-medium transition-colors duration-200 px-4 py-2',
          isOpen
            ? 'text-[#242424] dark:text-white'
            : 'text-[#616161] hover:text-[#242424] dark:text-[#adadad] dark:hover:text-white'
        )}
      >
        {item.label}
        <ChevronDown
          className={cn('h-4 w-4 transition-transform duration-200', isOpen && 'rotate-180')}
        />
      </button>

      {item.megaMenu ? (
        <MegaMenu item={item} isOpen={isOpen} onClose={onClose} />
      ) : (
        <NavDropdown item={item} isOpen={isOpen} onClose={onClose} />
      )}
    </div>
  )
}

export function LandingNav() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [mobileExpandedItems, setMobileExpandedItems] = useState<string[]>([])
  const [hidden, setHidden] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const lastY = useRef(0)

  // Hide the bar while scrolling down, bring it back smoothly on any scroll
  // up (blue2purple behavior). Never hide while a menu is open.
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      const delta = y - lastY.current
      setScrolled(y > 24)
      if (y < 80) {
        setHidden(false)
      } else if (delta > 6) {
        setHidden(true)
      } else if (delta < -6) {
        setHidden(false)
      }
      lastY.current = y
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (openDropdown || mobileMenuOpen) setHidden(false)
  }, [openDropdown, mobileMenuOpen])

  // Close any open menu with Escape.
  useEffect(() => {
    if (!openDropdown) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpenDropdown(null)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [openDropdown])

  const toggleMobileExpanded = (label: string) => {
    setMobileExpandedItems((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    )
  }

  // Flatten mega menu columns to children for mobile
  const getMobileChildren = (item: NavItem): NavChild[] => {
    if (item.children) return item.children
    if (item.sections) return item.sections.flatMap((s) => s.items)
    if (item.columns) return item.columns.flat()
    return []
  }

  const getMobileSections = (item: NavItem): NavSection[] | null => {
    if (item.sections) return item.sections
    return null
  }

  const megaOpen = Boolean(
    openDropdown && navigation.find((n) => n.label === openDropdown && (n.megaMenu || n.children))
  )
  const megaActive = Boolean(
    openDropdown && navigation.find((n) => n.label === openDropdown && n.megaMenu)
  )

  // One white sheet behind bar + open panel (the reference's .nav-bg): its
  // height grows from 0 to bar + panel, so surface and menu arrive as one
  // move instead of the bar filling first and the panel following.
  const navRef = useRef<HTMLElement>(null)
  const [sheetHeight, setSheetHeight] = useState(0)
  useLayoutEffect(() => {
    if (!megaActive) {
      setSheetHeight(0)
      return
    }
    const measure = () => {
      const nav = navRef.current
      const panel = nav?.querySelector<HTMLElement>('[data-mega-open="true"]')
      if (nav && panel) setSheetHeight(nav.offsetHeight + panel.offsetHeight)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [megaActive, openDropdown])

  return (
    <>
      {/* Dim and defocus the page while a menu is open (blue2purple). Lives
          outside the nav: its backdrop-filter would otherwise trap this
          fixed layer inside the bar. */}
      {megaOpen && (
        <div
          aria-hidden
          onClick={() => setOpenDropdown(null)}
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] backdrop-fade"
        />
      )}
    <nav
      ref={navRef}
      onMouseLeave={() => setOpenDropdown(null)}
      className={cn(
        // Tailwind v4's -translate-y-full sets the CSS `translate` property,
        // so it must be in the transition list or hide/show snaps.
        'sticky top-0 z-50 transition-[translate,opacity,background-color,border-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] border-b',
        // Transparent over the hero; the surface returns on scroll or when
        // any menu opens, separating the bar from the content below.
        scrolled || mobileMenuOpen || (openDropdown && !megaActive)
          ? 'bg-white/98 dark:bg-[#141414]/98 backdrop-blur-xl border-[#f0f0f0]/80 dark:border-[#292929]/80'
          : 'bg-transparent border-transparent',
        hidden && !mobileMenuOpen && '-translate-y-full opacity-0'
      )}
    >
      {/* Negative z inside the nav's stacking context: above the bar's own
          (possibly transparent) background, beneath logo, links and panel. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 bg-white dark:bg-[#141414] transition-[height] ease-[cubic-bezier(0.22,1,0.36,1)]"
        style={{ height: sheetHeight, transitionDuration: sheetHeight ? '450ms' : '300ms' }}
      />
      {/* Fixed 72px bar height: the home hero offsets itself by the same
          amount (-mt-[72px]) to run underneath the transparent bar. */}
      <div className="landing-container">
        <div className="flex h-[72px] items-center justify-between">
          {/* Left group: logo, divider, links (blue2purple layout) */}
          <div className="flex items-center gap-6">
            <Link to="/" aria-label="Templatean home" className="flex-shrink-0">
              <LogoWordmark size="md" />
            </Link>

            <span aria-hidden className="hidden lg:block h-6 w-px bg-[#e0e0e0] dark:bg-[#333]" />

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navigation.map((item) => (
                <NavItemComponent
                  key={item.label}
                  item={item}
                  isOpen={openDropdown === item.label}
                  onOpen={() => setOpenDropdown(item.label)}
                  onClose={() => setOpenDropdown(null)}
                />
              ))}
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm font-medium text-[#616161] hover:text-[#1a1a1a] dark:text-[#999] dark:hover:text-white transition-colors duration-200 px-3 py-2"
            >
              Sign in
            </Link>
            <Link to="/register" className="landing-cta landing-cta--solid landing-cta--sm landing-btn-sweep gap-2">
              Start free trial
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
            </Link>
          </div>

          {/* Mobile Navigation Toggle */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-[#f0f0f0] dark:hover:bg-[#3d3d3d] transition-colors duration-200"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-[#616161] dark:text-[#adadad]" />
              ) : (
                <Menu className="h-6 w-6 text-[#616161] dark:text-[#adadad]" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={cn(
          'lg:hidden overflow-hidden transition-all duration-300 border-t border-[#e0e0e0] dark:border-[#3d3d3d]',
          mobileMenuOpen ? 'max-h-[calc(100vh-64px)] opacity-100' : 'max-h-0 opacity-0 border-t-0'
        )}
      >
        <div className="px-6 py-4 space-y-1 bg-white dark:bg-[#141414]">
          {navigation.map((item) => {
            const children = getMobileChildren(item)
            const hasChildren = children.length > 0

            return (
              <div key={item.label}>
                {hasChildren ? (
                  <>
                    <button
                      onClick={() => toggleMobileExpanded(item.label)}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-[#424242] dark:text-[#d1d1d1] hover:bg-[#fafafa] dark:hover:bg-[#292929] rounded-lg transition-colors duration-200"
                    >
                      {item.label}
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 transition-transform duration-200',
                          mobileExpandedItems.includes(item.label) && 'rotate-180'
                        )}
                      />
                    </button>
                    <div
                      className={cn(
                        'overflow-hidden transition-all duration-200',
                        mobileExpandedItems.includes(item.label)
                          ? 'max-h-[800px] opacity-100'
                          : 'max-h-0 opacity-0'
                      )}
                    >
                      <div className="pl-4 py-1 space-y-1">
                        {getMobileSections(item) ? (
                          getMobileSections(item)?.map((section, sectionIndex) => (
                            <div key={section.title || sectionIndex}>
                              {section.title && (
                                <p className="text-xs font-semibold uppercase tracking-wider text-[#707070] dark:text-[#999] mt-3 mb-2 px-3">
                                  {section.title}
                                </p>
                              )}
                              {section.items.map((child) => (
                                <Link
                                  key={child.href}
                                  to={child.href}
                                  onClick={() => setMobileMenuOpen(false)}
                                  className="block px-3 py-2 text-sm text-[#616161] dark:text-[#adadad] hover:text-[#242424] dark:hover:text-white hover:bg-[#fafafa] dark:hover:bg-[#292929] rounded-lg transition-colors duration-200"
                                >
                                  {child.label}
                                </Link>
                              ))}
                            </div>
                          ))
                        ) : (
                          children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block px-3 py-2 text-sm text-[#616161] dark:text-[#adadad] hover:text-[#242424] dark:hover:text-white hover:bg-[#fafafa] dark:hover:bg-[#292929] rounded-lg transition-colors duration-200"
                            >
                              {child.label}
                            </Link>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.href || '/'}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2.5 text-sm font-medium text-[#424242] dark:text-[#d1d1d1] hover:bg-[#fafafa] dark:hover:bg-[#292929] rounded-lg transition-colors duration-200"
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            )
          })}

          {/* Mobile Auth Actions */}
          <div className="pt-4 mt-4 border-t border-[#e0e0e0] dark:border-[#3d3d3d] space-y-2">
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2.5 text-sm font-medium text-[#424242] dark:text-[#d1d1d1] hover:bg-[#fafafa] dark:hover:bg-[#292929] rounded-lg transition-colors duration-200"
            >
              Sign in
            </Link>
            <div className="px-3">
              <Link
                to="/register"
                onClick={() => setMobileMenuOpen(false)}
                className="landing-cta landing-cta--solid landing-cta--md landing-btn-sweep w-full"
              >
                Start free trial
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
    </>
  )
}

export default LandingNav
